// Dashboard › Atividade da equipe › Resumo por status (specs/20): quantos clientes há em cada status
// e a última operação que levou cada um até lá.
import { GRUPOS_STATUS, STATUS_CLIENTE, STATUS_EQUIPE_MAX, type CorStatus, type GrupoStatus } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, apiBlob, mensagemErro } from '../api';
import { Carregando, Erro, Icon } from '../components/ui';
import { data, moeda, num } from '../format';
import { CORES_UI, StatusBadge } from '../status';

type Periodo = 'hoje' | 'ontem' | '7d';
interface StatusResumo { id: string; nome: string; cor: CorStatus; grupo: GrupoStatus; clientes: number; mudaramPara: number; sairam: number; novos: number }
interface Linha {
  clienteId: string; cliente: string; empresa: string | null; vendedor: string | null; origemCadastro: string; desde: string;
  entrada: 'INICIAL' | 'MUDANCA'; deNome: string | null; deId: string | null; arquivado: boolean | null; por: string | null;
  canal: Canal | null; observacao: string | null; vendaValor: string | null; vendaData: string | null;
}
type Canal = 'WHATSAPP' | 'EMAIL' | 'LIGACAO' | 'PRESENCIAL';
interface Trilha {
  inicio: { quando: string; status: string; origem: string; por: string | null };
  trocas: Array<{ quando: string; de: string; para: string; por: string | null; canal: Canal | null; arquivado: boolean | null }>;
}

const CANAL: Record<Canal, string> = { WHATSAPP: 'WhatsApp', EMAIL: 'E-mail', LIGACAO: 'Ligação', PRESENCIAL: 'Visita' };
const ORIGEM: Record<string, string> = { MANUAL: 'cadastro', IMPORTACAO: 'importação de planilha', PROSPECCAO: 'importado da prospecção', API: 'integração' };
const ROTULO: Record<Periodo, string> = { hoje: 'hoje', ontem: 'ontem', '7d': 'em 7 dias' };
const iniciais = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
function haQuanto(iso: string) {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (d <= 0) return 'hoje';
  if (d === 1) return 'ontem';
  if (d < 30) return `há ${d} dias`;
  const m = Math.floor(d / 30);
  return m < 12 ? `há ${m} ${m === 1 ? 'mês' : 'meses'}` : `há ${Math.floor(m / 12)} ano${m >= 24 ? 's' : ''}`;
}
/** Como o cliente entrou no status, em uma frase. */
function como(l: Linha) {
  if (l.entrada === 'INICIAL') return `Status inicial · ${ORIGEM[l.origemCadastro] ?? 'cadastro'}${l.por ? ` (${l.por})` : ''}`;
  if (l.arquivado) return `Status “${l.deNome}” arquivado${l.por ? ` · ${l.por}` : ''}`;
  return `${l.canal ? CANAL[l.canal] : 'Edição da ficha'}${l.por ? ` · ${l.por}` : ''}`;
}

export function ResumoStatus({ periodo, vendedores }: { periodo: Periodo; vendedores: Array<{ id: string; nome: string }> }) {
  const [usuarioId, setUsuarioId] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [escopo, setEscopo] = useState<'periodo' | 'todos'>('periodo');
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [busca, setBusca] = useState('');
  const [origemId, setOrigemId] = useState('');
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [erroCsv, setErroCsv] = useState<string | null>(null);

  useEffect(() => { const t = setTimeout(() => setBusca(buscaDigitada.trim()), 300); return () => clearTimeout(t); }, [buscaDigitada]);

  const base = new URLSearchParams({ periodo });
  if (usuarioId) base.set('usuarioId', usuarioId);
  const resumo = useQuery({
    queryKey: ['equipe-status', base.toString()],
    queryFn: () => api<{ data: StatusResumo[] }>(`/dashboard/equipe/status?${base}`),
    placeholderData: (p) => p,
    refetchInterval: periodo === 'hoje' ? 60_000 : false,
  });
  const lista = resumo.data?.data ?? [];
  // padrão: o status com mais entradas no período; senão o primeiro do grupo Ativo
  const selecionado = lista.find((s) => s.id === sel)
    ?? [...lista].sort((a, b) => (b.mudaramPara + b.novos) - (a.mudaramPara + a.novos))[0]
    ?? lista.find((s) => s.grupo === 'ATIVO');
  const escolher = (id: string) => { setSel(id); setOrigemId(''); setAbertos(new Set()); };

  const qs = new URLSearchParams(base);
  qs.set('escopo', escopo);
  if (busca.length >= 2) qs.set('busca', busca);
  if (origemId) qs.set('origemId', origemId);
  const clientes = useQuery({
    queryKey: ['equipe-status-clientes', selecionado?.id, qs.toString()],
    queryFn: () => api<{ data: Linha[]; total: number }>(`/dashboard/equipe/status/${selecionado!.id}/clientes?${qs}`),
    enabled: !!selecionado,
    placeholderData: (p) => p,
  });
  const linhas = clientes.data?.data ?? [];
  const total = clientes.data?.total ?? 0;
  const corDe = (nome: string | null) => lista.find((s) => s.nome === nome)?.cor ?? 'cinza';
  const alternar = (id: string) => setAbertos((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function exportar() {
    if (!selecionado) return;
    setErroCsv(null);
    try {
      const blob = await apiBlob(`/dashboard/equipe/status/${selecionado.id}/clientes?${qs}&formato=csv`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `status-${selecionado.nome}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErroCsv(mensagemErro(e));
    }
  }

  if (resumo.isLoading) return <Carregando />;
  if (resumo.error) return <Erro erro={mensagemErro(resumo.error)} />;

  return (
    <div className="col" style={{ gap: 18 }}>
      <label className="row" style={{ gap: 8, alignItems: 'center', alignSelf: 'flex-end' }}><span className="small faint">Carteira de</span>
        <select className="select" style={{ minWidth: 200 }} value={usuarioId} onChange={(e) => { setUsuarioId(e.target.value); setAbertos(new Set()); }}>
          <option value="">Todos os vendedores</option>
          {vendedores.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
        </select>
      </label>

      {STATUS_CLIENTE.map((g) => {
        const itens = lista.filter((s) => s.grupo === g);
        if (!itens.length) return null;
        return (
          <section key={g} className="col" style={{ gap: 10 }} aria-label={GRUPOS_STATUS[g].nome}>
            <div className="status-grupo" style={{ paddingTop: 0 }}>
              <strong>{GRUPOS_STATUS[g].nome}</strong>
              <span className="small faint">{GRUPOS_STATUS[g].descricao}</span>
              <span className="num small muted" style={{ marginLeft: 'auto' }}>{num(itens.reduce((a, s) => a + s.clientes, 0))} clientes</span>
            </div>
            <div className="cards-status">
              {itens.map((s) => {
                const entraram = s.mudaramPara + s.novos;
                return (
                  <button key={s.id} type="button" className="card-status" aria-pressed={selecionado?.id === s.id} onClick={() => escolher(s.id)}>
                    <span className="row" style={{ gap: 8, alignItems: 'center', fontWeight: 500, fontSize: 13.5 }}>
                      <span className="status-dot" style={{ background: CORES_UI[s.cor].fg, width: 8, height: 8 }} aria-hidden="true" />{s.nome}
                    </span>
                    <span className="num card-status-n">{num(s.clientes)}</span>
                    <span className="row small" style={{ gap: 10 }}>
                      {entraram > 0 && <span style={{ color: 'var(--green-text)' }} title={s.novos ? `${s.mudaramPara} por mudança de status · ${s.novos} clientes novos` : undefined}>▲ {entraram} {entraram === 1 ? 'entrou' : 'entraram'}</span>}
                      {s.sairam > 0 && <span style={{ color: 'var(--red-text)' }}>▼ {s.sairam} {s.sairam === 1 ? 'saiu' : 'saíram'}</span>}
                      {!entraram && !s.sairam && <span className="faint">sem mudança {ROTULO[periodo]}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {selecionado && (
        <section className="card flush" aria-labelledby="h-status-clientes">
          <div className="card-head" style={{ padding: '16px 18px 0', flexWrap: 'wrap', gap: 12 }}>
            <h2 id="h-status-clientes" className="row" style={{ gap: 10, alignItems: 'center' }}>Clientes em <StatusBadge nome={selecionado.nome} cor={selecionado.cor} /></h2>
            <span className="small faint">
              {escopo === 'periodo' ? `${num(total)} ${total === 1 ? 'entrou' : 'entraram'} ${ROTULO[periodo]}` : `${num(total)} no status · mais recentes primeiro`}
            </span>
            <div className="seg" role="group" aria-label="Quais clientes" style={{ marginLeft: 'auto' }}>
              <button type="button" aria-pressed={escopo === 'periodo'} onClick={() => { setEscopo('periodo'); setAbertos(new Set()); }}>Entraram {ROTULO[periodo]}</button>
              <button type="button" aria-pressed={escopo === 'todos'} onClick={() => { setEscopo('todos'); setAbertos(new Set()); }}>Todos no status</button>
            </div>
          </div>
          <div className="row" style={{ gap: 10, padding: '12px 18px 0', alignItems: 'flex-end' }}>
            <label className="field" style={{ flex: '1 1 240px', maxWidth: 360 }}><span>Cliente</span>
              <input className="input" placeholder="Nome ou empresa" value={buscaDigitada} onChange={(e) => setBuscaDigitada(e.target.value)} />
            </label>
            <label className="field"><span>Veio de</span>
              <select className="select" value={origemId} onChange={(e) => setOrigemId(e.target.value)}>
                <option value="">Qualquer status</option>
                {lista.filter((s) => s.id !== selecionado.id).map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </label>
            <button type="button" className="btn" style={{ marginLeft: 'auto' }} disabled={!linhas.length} onClick={() => void exportar()}><Icon name="download" size={14} />Exportar CSV</button>
          </div>
          <div style={{ padding: '0 18px' }}><Erro erro={erroCsv} /></div>
          {total > STATUS_EQUIPE_MAX && (
            <div className="alert warn" style={{ margin: '12px 18px 0' }}><Icon name="alerta" /><span>Mostrando os {num(STATUS_EQUIPE_MAX)} mais recentes. Use a busca ou os filtros para achar os demais.</span></div>
          )}
          {clientes.isLoading ? <Carregando /> : clientes.error ? <Erro erro={mensagemErro(clientes.error)} /> : !linhas.length ? (
            <div className="empty">{escopo === 'periodo' ? `Nenhum cliente entrou em “${selecionado.nome}” ${ROTULO[periodo]}. Veja “Todos no status”.` : 'Nenhum cliente com esses filtros.'}</div>
          ) : (
            <div className="table-wrap">
              <table className="tabela-ce" style={{ minWidth: 1000 }}>
                <thead><tr><th>Cliente</th><th>Vendedor</th><th>Neste status desde</th><th>O que levou a este status</th><th><span className="sr-only">Detalhes</span></th></tr></thead>
                <tbody>
                  {linhas.map((l) => {
                    const aberta = abertos.has(l.clienteId);
                    return (
                      <Fragment key={l.clienteId}>
                        <tr className={aberta ? 'aberta' : undefined}>
                          <td><Link to={`/clientes/${l.clienteId}`} style={{ fontWeight: 500 }}>{l.cliente}</Link>{l.empresa && <><br /><span className="small faint">{l.empresa}</span></>}</td>
                          <td>{l.vendedor ? <span className="row" style={{ gap: 8, flexWrap: 'nowrap' }}><span className="av sm">{iniciais(l.vendedor)}</span>{l.vendedor}</span> : <span className="faint">Sem vendedor</span>}</td>
                          <td><span className="num small">{dataHora(l.desde)}</span><br /><span className="small faint">{haQuanto(l.desde)}</span></td>
                          <td>
                            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                              {l.entrada === 'MUDANCA' && l.deNome && (
                                <span className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
                                  <StatusBadge nome={l.deNome} cor={corDe(l.deNome)} /><span className="faint" aria-label="para">→</span>
                                  <StatusBadge nome={selecionado.nome} cor={selecionado.cor} />
                                </span>
                              )}
                              <span className="small" style={{ color: 'var(--text-2)' }}>{como(l)}</span>
                              {l.vendaValor !== null && <span className="badge green">Venda {moeda(l.vendaValor)}</span>}
                            </div>
                            {l.observacao && <div className="small muted resumo-ce">{l.observacao}</div>}
                          </td>
                          <td>
                            <button type="button" className="btn sm icon" aria-expanded={aberta} aria-label={`${aberta ? 'Fechar' : 'Ver'} detalhes de ${l.cliente}`} onClick={() => alternar(l.clienteId)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={aberta ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} /></svg>
                            </button>
                          </td>
                        </tr>
                        {aberta && (
                          <tr className="detalhe-ce">
                            <td colSpan={5}>
                              <div className="detalhe-grid">
                                <div className="col" style={{ gap: 8, minWidth: 0 }}>
                                  <span className="rotulo">Última operação</span>
                                  <span className="small">
                                    {l.entrada === 'INICIAL'
                                      ? `Entrou em “${selecionado.nome}” no cadastro (${ORIGEM[l.origemCadastro] ?? 'cadastro'}) em ${dataHora(l.desde)}${l.por ? `, por ${l.por}` : ''}.`
                                      : `${l.deNome} → ${selecionado.nome} em ${dataHora(l.desde)}, ${l.arquivado ? 'ao arquivar o status anterior' : l.canal ? `num contato por ${CANAL[l.canal]}` : 'na edição da ficha'}${l.por ? `, por ${l.por}` : ''}.`}
                                  </span>
                                  {l.observacao && <div className="obs-ce">{l.observacao}</div>}
                                  {l.vendaValor !== null && (
                                    <span className="small"><span className="faint">Venda</span> <strong className="num" style={{ color: 'var(--green-text)', fontWeight: 500 }}>{moeda(l.vendaValor)}</strong>{l.vendaData ? ` · data da venda ${data(l.vendaData)}` : ''}</span>
                                  )}
                                  <div><Link className="btn sm" to={`/clientes/${l.clienteId}`}>Abrir ficha</Link></div>
                                </div>
                                <TrilhaStatus clienteId={l.clienteId} cor={(n) => CORES_UI[corDe(n)].fg} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function TrilhaStatus({ clienteId, cor }: { clienteId: string; cor: (nome: string) => string }) {
  const q = useQuery({ queryKey: ['trilha-status', clienteId], queryFn: () => api<Trilha>(`/dashboard/equipe/clientes/${clienteId}/trilha-status`) });
  const t = q.data;
  return (
    <div className="col" style={{ gap: 8, minWidth: 0 }}>
      <span className="rotulo">Caminho do cliente</span>
      {q.isLoading ? <Carregando /> : q.error ? <Erro erro={mensagemErro(q.error)} /> : t && (
        <ol className="trilha-status">
          <li>
            <span className="num small faint">{dataHora(t.inicio.quando)}</span>
            <span className="pt" style={{ background: cor(t.inicio.status) }} aria-hidden="true" />
            <span className="small"><strong style={{ fontWeight: 500 }}>{t.inicio.status}</strong> <span className="faint">· {ORIGEM[t.inicio.origem] ?? 'cadastro'}{t.inicio.por ? ` (${t.inicio.por})` : ''}</span></span>
          </li>
          {t.trocas.map((x, i) => (
            <li key={i}>
              <span className="num small faint">{dataHora(x.quando)}</span>
              <span className="pt" style={{ background: cor(x.para) }} aria-hidden="true" />
              <span className="small"><strong style={{ fontWeight: 500 }}>{x.para}</strong> <span className="faint">· {x.arquivado ? `“${x.de}” arquivado` : x.canal ? CANAL[x.canal] : 'edição da ficha'}{x.por ? ` · ${x.por}` : ''}</span></span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
